import axios, { type AxiosProgressEvent } from "axios";
import * as Sentry from "@sentry/react";

/**
 * Where a presign tells the browser to send a file: a CloudFront-signed PUT on
 * deployed envs, or an S3 presigned POST with signed form fields (local / MinIO).
 * Every presign endpoint answers this shape under `upload_data`.
 */
export interface IPresignedUpload {
  url: string;
  fields?: Record<string, string>;
  method?: "PUT" | "POST";
}

/** How much of the file has reached storage, as a fraction from 0 to 1. */
type UploadProgressHandler = (fraction: number) => void;

export interface PresignedUploadOptions {
  /**
   * Called as the browser sends the file. A file can be up to 1 GB, which
   * takes minutes, so the caller shows how far along it is rather than a
   * spinner (NG-3279).
   */
  onProgress?: UploadProgressHandler;
}

/**
 * The one upload to storage every presigned flow uses (NG-3276).
 *
 * The request goes straight to storage, outside the shared API client, so the
 * client's response interceptor never sees it. A broken upload path used to be
 * caught by the caller, logged and shown as a toast, and nothing more: Sentry
 * Logs got a line, but no issue was created, so no alert fired while every
 * upload on prod and Canada failed for three days (NG-3260). A failure here is
 * now captured as one Sentry error, grouped by method and outcome, then
 * rethrown for the caller to handle as before.
 */
export async function uploadToPresignedUrl(
  upload: IPresignedUpload,
  file: File,
  { onProgress }: PresignedUploadOptions = {},
): Promise<void> {
  const method = upload.method === "PUT" ? "PUT" : "POST";
  const progress = onProgress
    ? { onUploadProgress: (event: AxiosProgressEvent) => onProgress(sentFraction(event, file)) }
    : {};
  try {
    if (method === "PUT") {
      await axios.put(upload.url, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
        ...progress,
      });
      return;
    }
    // S3's presigned POST takes its signed policy fields first and the file last,
    // under the key `file`.
    const form = new FormData();
    for (const [key, value] of Object.entries(upload.fields ?? {})) {
      form.append(key, value);
    }
    form.append("file", file);
    await axios.post(upload.url, form, progress);
  } catch (error) {
    captureUploadFailure(error, method, upload.url);
    throw error;
  }
}

/**
 * The share of the request body sent so far. A presigned POST's body is the
 * signed fields and the file, so its total is a little over the file's size;
 * when the browser reports no total, the file's size stands in.
 */
function sentFraction(event: AxiosProgressEvent, file: File): number {
  const total = event.total || file.size;
  if (!total) return 0;
  return Math.min(1, Math.max(0, event.loaded / total));
}

/** Raised to Sentry in place of the AxiosError, whose config holds the signed URL. */
export class PresignedUploadError extends Error {
  constructor(
    readonly method: "PUT" | "POST",
    readonly outcome: string,
  ) {
    super(`Upload to storage failed: ${method} ${outcome}`);
    this.name = "PresignedUploadError";
  }
}

/**
 * `network_error` (no answer, which is how a CORS refusal reads), `timeout`, or
 * `http_<status>`. Unlike an API call, a 4xx from storage is captured too: it is
 * not an answer the app asked for and handles, but a broken upload path (an
 * expired or mis-signed URL, a bucket policy), which is exactly what nobody heard
 * about in NG-3260.
 */
export function uploadFailureOutcome(error: unknown): string | null {
  if (axios.isCancel(error)) return null;
  if (!axios.isAxiosError(error)) return "unknown";
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return "timeout";
  const status = error.response?.status;
  return status === undefined ? "network_error" : `http_${status}`;
}

function captureUploadFailure(error: unknown, method: "PUT" | "POST", url: string): void {
  const outcome = uploadFailureOutcome(error);
  if (outcome === null) return;
  Sentry.captureException(new PresignedUploadError(method, outcome), {
    fingerprint: ["presigned-upload", method, outcome],
    tags: { "upload.method": method, "upload.outcome": outcome, "upload.host": hostOf(url) },
  });
}

/** The host alone: the path names the file and the query string is the signature. */
function hostOf(url: string): string {
  return /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(url)?.[1] ?? "unknown";
}
